import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { apiFetch } from "./api/client";
import SettingsSavedNotice from "./components/SettingsSavedNotice";
import UserIdentityLabel from "./components/UserIdentityLabel";
import { useAuth } from "./authContext";
import { useCurrentUser } from "./currentUserContext";

const DEFAULT_ENABLED_VALUE = 10;
const MAX_SETTING_VALUE = 100;
const MAX_RADIUS_KM = 500;
const DEFAULT_RADIUS_KM = 25;
const RADIUS_OPTIONS = [1, 10, 25, 50, 100, 200, 500];

function roundCurrency(value) {
  return Math.round((Number(value || 0) + Number.EPSILON) * 100) / 100;
}

function clampCurrency(value) {
  const numericValue = Number(value);

  if (!Number.isFinite(numericValue) || numericValue < 0) {
    return 0;
  }

  return Math.min(roundCurrency(numericValue), MAX_SETTING_VALUE);
}

function clampRadiusKm(value) {
  const numericValue = Number.parseInt(String(value ?? ""), 10);

  if (!Number.isInteger(numericValue) || numericValue < 0) {
    return 0;
  }

  return Math.min(numericValue, MAX_RADIUS_KM);
}

function getSelectableRadiusKm(value) {
  const numericValue = clampRadiusKm(value);

  if (RADIUS_OPTIONS.includes(numericValue)) {
    return numericValue;
  }

  return DEFAULT_RADIUS_KM;
}

function normalizeUserIdList(value, currentUserId = null) {
  const values = Array.isArray(value)
    ? value
    : String(value ?? "")
        .split(",")
        .map((entry) => entry.trim())
        .filter(Boolean);

  const currentUserNumericId = Number.parseInt(String(currentUserId ?? ""), 10);
  const normalizedUserIds = [];
  const seenUserIds = new Set();

  values.forEach((entry) => {
    const parsedValue = Number.parseInt(String(entry ?? ""), 10);

    if (!Number.isInteger(parsedValue) || parsedValue < 1 || parsedValue === currentUserNumericId || seenUserIds.has(parsedValue)) {
      return;
    }

    seenUserIds.add(parsedValue);
    normalizedUserIds.push(parsedValue);
  });

  return normalizedUserIds.sort((leftValue, rightValue) => leftValue - rightValue);
}

function sortSocialUsers(users = []) {
  return [...users].sort((leftUser, rightUser) => {
    const usernameComparison = String(leftUser?.username || "").localeCompare(String(rightUser?.username || ""), undefined, {
      sensitivity: "base"
    });

    if (usernameComparison !== 0) {
      return usernameComparison;
    }

    return Number(leftUser?.id || 0) - Number(rightUser?.id || 0);
  });
}

function parseCoordinate(value, minValue, maxValue) {
  const numericValue = Number(value);

  if (!Number.isFinite(numericValue) || numericValue < minValue || numericValue > maxValue) {
    return null;
  }

  return Math.round((numericValue + Number.EPSILON) * 1000000) / 1000000;
}

function formatCurrency(value) {
  return `${clampCurrency(value).toLocaleString(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  })} EUR`;
}

function formatRadius(value) {
  const numericValue = clampRadiusKm(value);

  if (numericValue <= 0) {
    return "Off";
  }

  return `${numericValue} km`;
}

function formatCoordinate(value) {
  const numericValue = Number(value);

  if (!Number.isFinite(numericValue)) {
    return "-";
  }

  return numericValue.toFixed(4);
}

function clampNumber(value, minValue, maxValue) {
  return Math.min(Math.max(value, minValue), maxValue);
}

function getMapPreviewBounds(locationLat, locationLng, radiusKm) {
  const lat = Number(locationLat);
  const lng = Number(locationLng);

  if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
    return null;
  }

  const effectiveRadiusKm = Math.min(Math.max(clampRadiusKm(radiusKm), 8), 250);
  const latDelta = effectiveRadiusKm / 111;
  const lngDelta = latDelta / Math.max(Math.cos((lat * Math.PI) / 180), 0.2);

  return {
    minLng: clampNumber(lng - lngDelta, -180, 180),
    minLat: clampNumber(lat - latDelta, -90, 90),
    maxLng: clampNumber(lng + lngDelta, -180, 180),
    maxLat: clampNumber(lat + latDelta, -90, 90)
  };
}

function getOpenStreetMapEmbedUrl(locationLat, locationLng, radiusKm) {
  const bounds = getMapPreviewBounds(locationLat, locationLng, radiusKm);

  if (!bounds) {
    return "";
  }

  const query = new URLSearchParams({
    bbox: `${bounds.minLng},${bounds.minLat},${bounds.maxLng},${bounds.maxLat}`,
    layer: "mapnik",
    marker: `${locationLat},${locationLng}`
  });

  return `https://www.openstreetmap.org/export/embed.html?${query.toString()}`;
}

function getOpenStreetMapUrl(locationLat, locationLng) {
  const lat = Number(locationLat);
  const lng = Number(locationLng);

  if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
    return "";
  }

  return `https://www.openstreetmap.org/?mlat=${lat}&mlon=${lng}#map=10/${lat}/${lng}`;
}

function createDraft(settings = {}) {
  const minSaleValue = clampCurrency(settings.minSaleValue);
  const minBuyValue = clampCurrency(settings.minBuyValue);

  return {
    isMinSaleEnabled: minSaleValue > 0,
    isMinBuyEnabled: minBuyValue > 0,
    minSaleValue: minSaleValue > 0 ? minSaleValue : DEFAULT_ENABLED_VALUE,
    minBuyValue: minBuyValue > 0 ? minBuyValue : DEFAULT_ENABLED_VALUE,
    isSocialEnabled: Boolean(settings.socialEnabled),
    socialFriendUserIds: normalizeUserIdList(settings.socialFriendUserIds ?? settings.socialUserIds),
    socialSelectedUserIds: normalizeUserIdList(settings.socialSelectedUserIds ?? settings.socialUserIds),
    radiusKm: clampRadiusKm(settings.radiusKm),
    locationLabel: String(settings.locationLabel || "").trim(),
    locationLat: parseCoordinate(settings.locationLat, -90, 90),
    locationLng: parseCoordinate(settings.locationLng, -180, 180)
  };
}

function formatSocialSummary(enabled) {
  return enabled ? "On" : "Off";
}

function SettingCard({
  className = "",
  title,
  description,
  enabled,
  value,
  onToggle,
  onValueChange
}) {
  return (
    <section className={`account-setting-card ${className}`.trim()}>
      <div className="account-setting-card__header">
        <div>
          <h2 className="account-setting-card__title">{title}</h2>
          <p className="account-setting-card__copy">{description}</p>
        </div>
        <label className="account-toggle">
          <input type="checkbox" checked={enabled} onChange={(event) => onToggle(event.target.checked)} />
          <span className="account-toggle__switch" aria-hidden="true" />
          <span className="account-toggle__label">{enabled ? "On" : "Off"}</span>
        </label>
      </div>

      <div className="account-setting-card__body">
        <div className="account-setting-card__value">{enabled ? formatCurrency(value) : "Off"}</div>
        <input
          className="account-setting-slider"
          type="range"
          min="0"
          max={String(MAX_SETTING_VALUE)}
          step="1"
          disabled={!enabled}
          value={value}
          onChange={(event) => onValueChange(clampCurrency(event.target.value))}
          aria-label={title}
        />
        <div className="account-setting-card__scale">
          <span>0 EUR</span>
          <span>{MAX_SETTING_VALUE} EUR</span>
        </div>
      </div>
    </section>
  );
}

function LocationSettingCard({
  className = "",
  locationLabel,
  locationLat,
  locationLng,
  radiusKm,
  searchQuery,
  isSearching,
  searchError,
  locationResults,
  onSearchQueryChange,
  onRadiusChange,
  onSelectLocation,
  onClearLocation
}) {
  const hasLocation = Boolean(locationLabel);
  const mapEmbedUrl = hasLocation ? getOpenStreetMapEmbedUrl(locationLat, locationLng, radiusKm) : "";
  const mapUrl = hasLocation ? getOpenStreetMapUrl(locationLat, locationLng) : "";
  const selectedRadiusKm = getSelectableRadiusKm(radiusKm);

  return (
    <section className={`account-setting-card account-setting-card--wide ${className}`.trim()}>
      <div className="account-setting-card__header account-setting-card__header--stacked">
        <div>
          <h2 className="account-setting-card__title">Trade radius</h2>
          <p className="account-setting-card__copy">
            Choose a city or area and set how far away TradeFinder should look for local matches. Trade suggestions only appear when both users accept the distance.
          </p>
        </div>
      </div>

      <div className="account-setting-card__body account-location-card__body">
        <div className="account-setting-card__value">{hasLocation ? locationLabel : "Off"}</div>
        <div className="account-location-card__meta-row">
          <label className="account-location-card__label" htmlFor="tradefinder-location-search">City or area</label>
          <div className="account-location-card__search-shell">
            <input
              id="tradefinder-location-search"
              className="account-location-card__input"
              type="search"
              value={searchQuery}
              onChange={(event) => onSearchQueryChange(event.target.value)}
              placeholder="Search for Copenhagen, Aarhus, Odense..."
              autoComplete="off"
            />
            {locationResults.length > 0 ? (
              <div className="account-location-card__dropdown account-location-card__results" role="listbox" aria-label="Location suggestions">
                {locationResults.map((entry) => (
                  <button
                    key={`${entry.label}:${entry.lat}:${entry.lng}`}
                    type="button"
                    className="account-location-card__result"
                    onClick={() => onSelectLocation(entry)}
                  >
                    <span className="account-location-card__result-label">{entry.label}</span>
                    <span className="account-location-card__result-meta">Use this location</span>
                  </button>
                ))}
              </div>
            ) : null}
          </div>
        </div>
        <div className="account-location-card__actions">
          <label className="account-location-card__label" htmlFor="tradefinder-radius-select">Radius</label>
          <select
            id="tradefinder-radius-select"
            className="account-location-card__select"
            value={selectedRadiusKm}
            onChange={(event) => onRadiusChange(clampRadiusKm(event.target.value))}
          >
            {RADIUS_OPTIONS.map((option) => (
              <option key={option} value={option}>{`${option} km`}</option>
            ))}
          </select>
          <button
            type="button"
            className="account-location-card__clear"
            disabled={!hasLocation}
            onClick={onClearLocation}
          >
            Clear location
          </button>
        </div>
        <div className="account-location-card__status-row">
          <span className="account-location-card__status-pill">
            Active radius: {hasLocation ? formatRadius(radiusKm) : "Off"}
          </span>
          {isSearching ? <span className="account-location-card__hint">Searching locations...</span> : null}
        </div>
        {hasLocation ? (
          <div className="account-location-card__preview">
            <div className="account-location-card__preview-header">
              <strong>{locationLabel}</strong>
              <span>{formatRadius(radiusKm)}</span>
            </div>
            {mapEmbedUrl ? (
              <div className="account-location-card__map-shell">
                <iframe
                  className="account-location-card__map"
                  src={mapEmbedUrl}
                  title={`Map preview for ${locationLabel}`}
                  loading="lazy"
                />
              </div>
            ) : null}
            <div className="account-location-card__preview-meta">
              <span>Lat {formatCoordinate(locationLat)}</span>
              <span>Lng {formatCoordinate(locationLng)}</span>
              <span>Matches require both users to allow the distance.</span>
            </div>
            {mapUrl ? (
              <div className="account-location-card__preview-actions">
                <a href={mapUrl} target="_blank" rel="noreferrer">Open full map</a>
              </div>
            ) : null}
          </div>
        ) : null}
        {searchError ? <div className="account-location-card__error">{searchError}</div> : null}
      </div>
    </section>
  );
}

function SocialSettingCard({
  className = "",
  enabled,
  friendUserIds,
  selectedUserIds,
  socialUsers,
  searchQuery,
  isLoading,
  searchError,
  onToggle,
  onSearchQueryChange,
  onAddUser,
  onToggleSelection,
  onRemoveUser,
  onClearSelection
}) {
  const containerRef = useRef(null);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const friendUserIdSet = useMemo(() => new Set(friendUserIds), [friendUserIds]);
  const selectedUserIdSet = useMemo(() => new Set(selectedUserIds), [selectedUserIds]);
  const selectedCount = selectedUserIds.length;
  const friendCount = friendUserIds.length;
  const normalizedQuery = String(searchQuery || "").trim();
  const selectedFriends = useMemo(
    () => sortSocialUsers(socialUsers.filter((user) => friendUserIdSet.has(Number(user.id)))),
    [friendUserIdSet, socialUsers]
  );
  const searchResults = useMemo(
    () => sortSocialUsers(socialUsers.filter((user) => !friendUserIdSet.has(Number(user.id)))),
    [friendUserIdSet, socialUsers]
  );

  useEffect(() => {
    if (normalizedQuery.length >= 2) {
      setIsDropdownOpen(true);
      return;
    }

    setIsDropdownOpen(false);
  }, [normalizedQuery]);

  useEffect(() => {
    if (!isDropdownOpen) {
      return undefined;
    }

    const handlePointerDown = (event) => {
      if (containerRef.current?.contains(event.target)) {
        return;
      }

      setIsDropdownOpen(false);
    };

    document.addEventListener("mousedown", handlePointerDown);
    return () => document.removeEventListener("mousedown", handlePointerDown);
  }, [isDropdownOpen]);

  return (
    <section className={`account-setting-card account-setting-card--wide ${className}`.trim()}>
      <div className="account-setting-card__header account-setting-card__header--stacked">
        <div>
          <h2 className="account-setting-card__title">Social</h2>
          <p className="account-setting-card__copy">
            Build a friend list for TradeFinder. When Social is on, only selected users are allowed through as trade matches.
          </p>
        </div>
        <label className="account-toggle">
          <input type="checkbox" checked={enabled} onChange={(event) => onToggle(event.target.checked)} />
          <span className="account-toggle__switch" aria-hidden="true" />
          <span className="account-toggle__label">{enabled ? "On" : "Off"}</span>
        </label>
      </div>

      <div className="account-setting-card__body account-social-card__body">
        <div className="account-setting-card__value">{formatSocialSummary(enabled, selectedCount, friendCount)}</div>
        <div className="account-social-card__controls">
          <div className="account-social-card__search-field">
            <label className="account-location-card__label" htmlFor="tradefinder-social-search">Find users</label>
            <div ref={containerRef} className="account-social-card__search-shell">
              <input
                id="tradefinder-social-search"
                className="account-location-card__input"
                type="search"
                value={searchQuery}
                onChange={(event) => onSearchQueryChange(event.target.value)}
                onFocus={() => {
                  if (String(searchQuery || "").trim().length >= 2) {
                    setIsDropdownOpen(true);
                  }
                }}
                placeholder="Search usernames..."
                autoComplete="off"
              />
              {isDropdownOpen ? (
                <div className="account-social-card__dropdown account-setting-select-menu" role="listbox" aria-label="Search users">
                  {isLoading ? (
                    <div className="account-social-card__dropdown-empty">Loading users...</div>
                  ) : null}
                  {!isLoading && searchResults.length === 0 && !searchError ? (
                    <div className="account-social-card__dropdown-empty">No users matched that search.</div>
                  ) : null}
                  {!isLoading && searchResults.map((user) => (
                    <div key={user.id} className="account-social-card__dropdown-row" role="option" aria-selected="false">
                      <div className="account-social-card__dropdown-copy">
                        <UserIdentityLabel username={user.username || `User ${user.id}`} tier={user.collectorTier || "tier1"} size="sm" color="#0f172a" fontWeight={700} truncateAt={10} />
                        <span>{user.listedCount || 0} listed cards</span>
                      </div>
                      <button
                        type="button"
                        className="account-social-card__add"
                        onClick={() => {
                          onAddUser(user);
                          onSearchQueryChange("");
                          setIsDropdownOpen(false);
                        }}
                      >
                        Add friend
                      </button>
                    </div>
                  ))}
                </div>
              ) : null}
            </div>
          </div>
          <button
            type="button"
            className="account-location-card__clear"
            disabled={friendCount === 0}
            onClick={onClearSelection}
          >
            Clear list
          </button>
        </div>
        <div className="account-location-card__status-row">
          <span className="account-location-card__status-pill">Friends: {friendCount}</span>
          <span className="account-location-card__status-pill">Selected: {selectedCount}</span>
          {isLoading ? <span className="account-location-card__hint">Loading users...</span> : null}
        </div>
        {searchError ? <div className="account-location-card__error">{searchError}</div> : null}
        <div className="account-social-card__table-shell">
          {selectedFriends.length > 0 ? (
            <table className="account-social-card__table">
              <thead>
                <tr>
                  <th aria-label="Selection" />
                  <th>Username</th>
                  <th>Cards</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {selectedFriends.map((user) => (
                  <tr key={user.id}>
                    <td>
                      <button
                        type="button"
                        className={`account-social-card__selection-button ${selectedUserIdSet.has(Number(user.id)) ? "account-social-card__selection-button--selected" : ""}`.trim()}
                        aria-pressed={selectedUserIdSet.has(Number(user.id))}
                        aria-label={`${selectedUserIdSet.has(Number(user.id)) ? "Deselect" : "Select"} ${user.username || `User ${user.id}`}`}
                        onClick={() => onToggleSelection(user)}
                      >
                        <span className={`account-social-card__check ${selectedUserIdSet.has(Number(user.id)) ? "account-social-card__check--selected" : ""}`.trim()} aria-hidden="true">
                          {selectedUserIdSet.has(Number(user.id)) ? "✓" : ""}
                        </span>
                      </button>
                    </td>
                    <td>
                      <UserIdentityLabel username={user.username || `User ${user.id}`} tier={user.collectorTier || "tier1"} size="sm" color="#0f172a" fontWeight={700} truncateAt={10} />
                    </td>
                    <td>{user.listedCount || 0}</td>
                    <td>
                      <button
                        type="button"
                        className="account-social-card__remove"
                        onClick={() => onRemoveUser(user)}
                      >
                        Remove
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div className="account-social-card__empty">
              Search for a user above and choose Add friend to build the friend list.
            </div>
          )}
        </div>
      </div>
    </section>
  );
}

export default function TradeFinderSettingsView() {
  const { selectedUserId, selectedUser } = useCurrentUser();
  const { user, refreshAuth } = useAuth();
  const [savedSettings, setSavedSettings] = useState({
    minSaleValue: 0,
    minBuyValue: 0,
    socialEnabled: false,
    socialFriendUserIds: [],
    socialSelectedUserIds: [],
    radiusKm: 0,
    locationLabel: "",
    locationLat: null,
    locationLng: null
  });
  const [draft, setDraft] = useState(createDraft());
  const [socialSearchQuery, setSocialSearchQuery] = useState("");
  const [socialUsers, setSocialUsers] = useState([]);
  const [socialSearchError, setSocialSearchError] = useState(null);
  const [isLoadingSocialUsers, setIsLoadingSocialUsers] = useState(false);
  const [locationQuery, setLocationQuery] = useState("");
  const [locationResults, setLocationResults] = useState([]);
  const [locationSearchError, setLocationSearchError] = useState(null);
  const [isSearchingLocation, setIsSearchingLocation] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);

  useEffect(() => {
    let isDisposed = false;

    async function loadSettings() {
      try {
        setLoading(true);
        const response = await apiFetch(`/api/account/tradefinder-settings/${selectedUserId}`, { userId: selectedUserId });
        const data = await response.json().catch(() => ({}));

        if (!response.ok) {
          throw new Error(data.error || "Could not load TradeFinder settings");
        }

        if (isDisposed) {
          return;
        }

        const nextSettings = {
          minSaleValue: clampCurrency(data?.settings?.minSaleValue),
          minBuyValue: clampCurrency(data?.settings?.minBuyValue),
          socialEnabled: Boolean(data?.settings?.socialEnabled),
          socialFriendUserIds: normalizeUserIdList(data?.settings?.socialFriendUserIds ?? data?.settings?.socialUserIds, selectedUserId),
          socialSelectedUserIds: normalizeUserIdList(data?.settings?.socialSelectedUserIds ?? data?.settings?.socialUserIds, selectedUserId),
          radiusKm: clampRadiusKm(data?.settings?.radiusKm),
          locationLabel: String(data?.settings?.locationLabel || "").trim(),
          locationLat: parseCoordinate(data?.settings?.locationLat, -90, 90),
          locationLng: parseCoordinate(data?.settings?.locationLng, -180, 180)
        };

        setSavedSettings(nextSettings);
        setDraft(createDraft(nextSettings));
        setLocationQuery(nextSettings.locationLabel || "");
        setSocialSearchQuery("");
        setSocialUsers([]);
        setSocialSearchError(null);
        setLocationResults([]);
        setLocationSearchError(null);
        setError(null);
      } catch (err) {
        if (!isDisposed) {
          console.error("TradeFinder settings load error:", err);
          setError(err.message || "Could not load TradeFinder settings");
        }
      } finally {
        if (!isDisposed) {
          setLoading(false);
        }
      }
    }

    loadSettings();

    return () => {
      isDisposed = true;
    };
  }, [selectedUserId]);

  useEffect(() => {
    if (loading) {
      return undefined;
    }

    const normalizedQuery = String(locationQuery || "").trim();
    const normalizedSelectedLocation = String(draft.locationLabel || "").trim();

    if (normalizedQuery.length < 2 || normalizedQuery === normalizedSelectedLocation) {
      setLocationResults([]);
      setLocationSearchError(null);
      setIsSearchingLocation(false);
      return undefined;
    }

    let isDisposed = false;
    const timeoutId = window.setTimeout(async () => {
      try {
        setIsSearchingLocation(true);
        setLocationSearchError(null);
        const response = await apiFetch(`/api/account/tradefinder-location-search?q=${encodeURIComponent(normalizedQuery)}`, { userId: selectedUserId });
        const data = await response.json().catch(() => ({}));

        if (!response.ok) {
          throw new Error(data.error || "Could not search locations");
        }

        if (!isDisposed) {
          setLocationResults(Array.isArray(data.results) ? data.results : []);
        }
      } catch (err) {
        if (!isDisposed) {
          setLocationResults([]);
          setLocationSearchError(err.message || "Could not search locations");
        }
      } finally {
        if (!isDisposed) {
          setIsSearchingLocation(false);
        }
      }
    }, 300);

    return () => {
      isDisposed = true;
      window.clearTimeout(timeoutId);
    };
  }, [draft.locationLabel, loading, locationQuery, selectedUserId]);

  useEffect(() => {
    if (loading) {
      return undefined;
    }

    if (draft.socialFriendUserIds.length === 0 && String(socialSearchQuery || "").trim().length < 2) {
      setSocialUsers([]);
      setIsLoadingSocialUsers(false);
      setSocialSearchError(null);
      return undefined;
    }

    let isDisposed = false;
    const timeoutId = window.setTimeout(async () => {
      try {
        setIsLoadingSocialUsers(true);
        setSocialSearchError(null);
        const params = new URLSearchParams();

        if (socialSearchQuery.trim()) {
          params.set("q", socialSearchQuery.trim());
        }

        if (draft.socialFriendUserIds.length > 0) {
          params.set("selected", draft.socialFriendUserIds.join(","));
        }

        params.set("limit", "12");

        const response = await apiFetch(`/api/account/tradefinder-social-users?${params.toString()}`, { userId: selectedUserId });
        const data = await response.json().catch(() => ({}));

        if (!response.ok) {
          throw new Error(data.error || "Could not load social users");
        }

        if (!isDisposed) {
          setSocialUsers(sortSocialUsers(Array.isArray(data.users) ? data.users : []));
        }
      } catch (err) {
        if (!isDisposed) {
          setSocialUsers([]);
          setSocialSearchError(err.message || "Could not load social users");
        }
      } finally {
        if (!isDisposed) {
          setIsLoadingSocialUsers(false);
        }
      }
    }, 220);

    return () => {
      isDisposed = true;
      window.clearTimeout(timeoutId);
    };
  }, [draft.socialFriendUserIds, loading, selectedUserId, socialSearchQuery]);

  const submissionSettings = useMemo(() => ({
    minSaleValue: draft.isMinSaleEnabled ? clampCurrency(draft.minSaleValue) : 0,
    minBuyValue: draft.isMinBuyEnabled ? clampCurrency(draft.minBuyValue) : 0,
    socialEnabled: Boolean(draft.isSocialEnabled),
    socialFriendUserIds: normalizeUserIdList(draft.socialFriendUserIds, selectedUserId),
    socialSelectedUserIds: normalizeUserIdList(draft.socialSelectedUserIds, selectedUserId)
      .filter((entry) => normalizeUserIdList(draft.socialFriendUserIds, selectedUserId).includes(entry)),
    radiusKm: clampRadiusKm(draft.radiusKm),
    locationLabel: draft.locationLabel || null,
    locationLat: draft.locationLat,
    locationLng: draft.locationLng
  }), [draft, selectedUserId]);

  const isDirty = submissionSettings.minSaleValue !== savedSettings.minSaleValue
    || submissionSettings.minBuyValue !== savedSettings.minBuyValue
    || submissionSettings.socialEnabled !== savedSettings.socialEnabled
    || submissionSettings.socialFriendUserIds.join(",") !== normalizeUserIdList(savedSettings.socialFriendUserIds, selectedUserId).join(",")
    || submissionSettings.socialSelectedUserIds.join(",") !== normalizeUserIdList(savedSettings.socialSelectedUserIds, selectedUserId).join(",")
    || submissionSettings.radiusKm !== savedSettings.radiusKm
    || submissionSettings.locationLabel !== (savedSettings.locationLabel || null)
    || submissionSettings.locationLat !== savedSettings.locationLat
    || submissionSettings.locationLng !== savedSettings.locationLng;
  const hasPendingLocationSelection = String(locationQuery || "").trim().length >= 2
    && String(locationQuery || "").trim() !== String(draft.locationLabel || "").trim();

  useEffect(() => {
    if (!notice) {
      return undefined;
    }

    const timeoutId = window.setTimeout(() => setNotice(null), 3600);
    return () => window.clearTimeout(timeoutId);
  }, [notice]);

  useEffect(() => {
    if (loading || saving || !isDirty || hasPendingLocationSelection) {
      return undefined;
    }

    const timeoutId = window.setTimeout(async () => {
      try {
        setSaving(true);
        setError(null);
        const response = await apiFetch(`/api/account/tradefinder-settings/${selectedUserId}`, {
          userId: selectedUserId,
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(submissionSettings)
        });
        const data = await response.json().catch(() => ({}));

        if (!response.ok) {
          throw new Error(data.error || "Could not save TradeFinder settings");
        }

        const nextSettings = {
          minSaleValue: clampCurrency(data?.settings?.minSaleValue),
          minBuyValue: clampCurrency(data?.settings?.minBuyValue),
          socialEnabled: Boolean(data?.settings?.socialEnabled),
          socialFriendUserIds: normalizeUserIdList(data?.settings?.socialFriendUserIds ?? data?.settings?.socialUserIds, selectedUserId),
          socialSelectedUserIds: normalizeUserIdList(data?.settings?.socialSelectedUserIds ?? data?.settings?.socialUserIds, selectedUserId),
          radiusKm: clampRadiusKm(data?.settings?.radiusKm),
          locationLabel: String(data?.settings?.locationLabel || "").trim(),
          locationLat: parseCoordinate(data?.settings?.locationLat, -90, 90),
          locationLng: parseCoordinate(data?.settings?.locationLng, -180, 180)
        };

        setSavedSettings(nextSettings);
        if (Number(selectedUserId) === Number(user?.id)) {
          await refreshAuth();
        }
        setNotice("Settings saved");
      } catch (err) {
        console.error("TradeFinder settings save error:", err);
        setError(err.message || "Could not save TradeFinder settings");
      } finally {
        setSaving(false);
      }
    }, 250);

    return () => window.clearTimeout(timeoutId);
  }, [hasPendingLocationSelection, isDirty, loading, refreshAuth, saving, selectedUserId, submissionSettings, user?.id]);

  return (
    <div className="account-shell">
      <div className="account-settings-page tradefinder-settings-page">
        <div className="account-breadcrumb">
          <Link to="/account">My Account</Link>
          <span>/</span>
          <span>TradeFinder Settings</span>
        </div>

        <div className="account-settings-panel__header">
          <div>
            <h1 className="account-title">TradeFinder Settings</h1>
            <p className="account-copy">
              Control how {selectedUser?.label || "this user"} appears in TradeFinder. A suggestion is only shown when both users meet each other&apos;s minimum values, and local filtering now requires that both users&apos; active radii allow the distance.
            </p>
          </div>
        </div>

        {error ? <div className="account-error">{error}</div> : null}

        {loading ? (
          <div className="account-loading">Loading settings...</div>
        ) : (
          <>
            <div className="account-setting-grid account-setting-grid--tradefinder">
              <div className="account-setting-grid__stack account-setting-grid__stack--tradefinder-values">
                <SettingCard
                  title="Minimum sale value"
                  description="This user is only shown when the other trader wants at least this much from this user&apos;s listings."
                  enabled={draft.isMinSaleEnabled}
                  value={draft.minSaleValue}
                  onToggle={(checked) => {
                    setDraft((currentState) => ({
                      ...currentState,
                      isMinSaleEnabled: checked,
                      minSaleValue: checked
                        ? Math.max(currentState.minSaleValue, DEFAULT_ENABLED_VALUE)
                        : currentState.minSaleValue
                    }));
                  }}
                  onValueChange={(nextValue) => {
                    setDraft((currentState) => ({
                      ...currentState,
                      minSaleValue: nextValue
                    }));
                  }}
                />

                <SettingCard
                  title="Minimum buy value"
                  description="This user is only shown when this user wants at least this much from the other trader&apos;s listings."
                  enabled={draft.isMinBuyEnabled}
                  value={draft.minBuyValue}
                  onToggle={(checked) => {
                    setDraft((currentState) => ({
                      ...currentState,
                      isMinBuyEnabled: checked,
                      minBuyValue: checked
                        ? Math.max(currentState.minBuyValue, DEFAULT_ENABLED_VALUE)
                        : currentState.minBuyValue
                    }));
                  }}
                  onValueChange={(nextValue) => {
                    setDraft((currentState) => ({
                      ...currentState,
                      minBuyValue: nextValue
                    }));
                  }}
                />
              </div>

              <LocationSettingCard
                className="account-setting-card--tradefinder-radius"
                locationLabel={draft.locationLabel}
                locationLat={draft.locationLat}
                locationLng={draft.locationLng}
                radiusKm={draft.radiusKm}
                searchQuery={locationQuery}
                isSearching={isSearchingLocation}
                searchError={locationSearchError}
                locationResults={locationResults}
                onSearchQueryChange={setLocationQuery}
                onRadiusChange={(nextRadiusKm) => {
                  setDraft((currentState) => ({
                    ...currentState,
                    radiusKm: nextRadiusKm
                  }));
                }}
                onSelectLocation={(entry) => {
                  setDraft((currentState) => ({
                    ...currentState,
                    radiusKm: currentState.radiusKm > 0 ? currentState.radiusKm : DEFAULT_RADIUS_KM,
                    locationLabel: entry.label,
                    locationLat: parseCoordinate(entry.lat, -90, 90),
                    locationLng: parseCoordinate(entry.lng, -180, 180)
                  }));
                  setLocationQuery(entry.label);
                  setLocationResults([]);
                  setLocationSearchError(null);
                }}
                onClearLocation={() => {
                  setDraft((currentState) => ({
                    ...currentState,
                    locationLabel: "",
                    locationLat: null,
                    locationLng: null,
                    radiusKm: 0
                  }));
                  setLocationQuery("");
                  setLocationResults([]);
                  setLocationSearchError(null);
                }}
              />

              <SocialSettingCard
                className="account-setting-card--tradefinder-social"
                enabled={draft.isSocialEnabled}
                friendUserIds={draft.socialFriendUserIds}
                selectedUserIds={draft.socialSelectedUserIds}
                socialUsers={socialUsers}
                searchQuery={socialSearchQuery}
                isLoading={isLoadingSocialUsers}
                searchError={socialSearchError}
                onToggle={(checked) => {
                  setDraft((currentState) => ({
                    ...currentState,
                    isSocialEnabled: checked
                  }));
                }}
                onSearchQueryChange={setSocialSearchQuery}
                onAddUser={(user) => {
                  setDraft((currentState) => {
                    const currentFriendIds = new Set(currentState.socialFriendUserIds);
                    const currentSelectedIds = new Set(currentState.socialSelectedUserIds);
                    const numericUserId = Number(user.id);

                    currentFriendIds.add(numericUserId);
                    currentSelectedIds.add(numericUserId);

                    return {
                      ...currentState,
                      socialFriendUserIds: [...currentFriendIds].sort((leftValue, rightValue) => leftValue - rightValue),
                      socialSelectedUserIds: [...currentSelectedIds].sort((leftValue, rightValue) => leftValue - rightValue)
                    };
                  });
                }}
                onToggleSelection={(user) => {
                  setDraft((currentState) => {
                    const currentSelectedIds = new Set(currentState.socialSelectedUserIds);
                    const numericUserId = Number(user.id);

                    if (currentSelectedIds.has(numericUserId)) {
                      currentSelectedIds.delete(numericUserId);
                    } else {
                      currentSelectedIds.add(numericUserId);
                    }

                    return {
                      ...currentState,
                      socialSelectedUserIds: [...currentSelectedIds]
                        .filter((entry) => currentState.socialFriendUserIds.includes(entry))
                        .sort((leftValue, rightValue) => leftValue - rightValue)
                    };
                  });
                }}
                onRemoveUser={(user) => {
                  setDraft((currentState) => ({
                    ...currentState,
                    socialFriendUserIds: currentState.socialFriendUserIds.filter((userId) => userId !== Number(user.id)),
                    socialSelectedUserIds: currentState.socialSelectedUserIds.filter((userId) => userId !== Number(user.id))
                  }));
                }}
                onClearSelection={() => {
                  setDraft((currentState) => ({
                    ...currentState,
                    socialFriendUserIds: [],
                    socialSelectedUserIds: []
                  }));
                }}
              />
            </div>
          </>
        )}
      </div>

      {notice ? <SettingsSavedNotice message={notice} /> : null}
    </div>
  );
}